export type Priority = 'high' | 'medium' | 'low';
export type TaskStatus = 'not-started' | 'in-progress' | 'completed';
export type TimerType = 'pomodoro' | 'manual';
export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
export type PaymentMethod = 'bank_transfer' | 'paypal' | 'stripe' | 'cash' | 'other';
export type Currency = 'EUR' | 'USD' | 'AED';

// Integration types
export type IntegrationProvider = 'github';
export type IntegrationStatus = 'active' | 'suspended' | 'disconnected';
export type AccountType = 'User' | 'Organization';
export type TaskOrigin = 'flowky' | IntegrationProvider;

// External task data from integrations (e.g., GitHub issues)
export interface ExternalTaskData {
  provider: IntegrationProvider;
  issueNumber?: number;
  repoFullName?: string;
  state?: string;
  stateReason?: string;
  labels?: string[];
  assignees?: string[];
  milestone?: string;
  isPullRequest?: boolean;
  htmlUrl?: string;
  author?: string;
}

// Base integration interface (provider-agnostic)
export interface BaseIntegration {
  id: string;
  provider: IntegrationProvider;
  status: IntegrationStatus;
  accountLogin: string;
  accountType: AccountType;
  accountId: string;
  avatarUrl?: string;
  clientId?: string; // Link integration to a specific client
  syncEnabled: boolean;
  syncRepos: string[];
  syncAssigneesOnly: boolean;
  lastSyncAt?: string;
  lastSyncStatus?: 'success' | 'failed';
  lastSyncError?: string;
  connectedAt: string;
  connectedBy: string;
  createdAt?: string;
  updatedAt?: string;
}

// GitHub-specific integration
export interface GitHubIntegration extends BaseIntegration {
  provider: 'github';
  installationId: number;
  webhooksEnabled: boolean;
}

// Union type for all integrations
export type Integration = GitHubIntegration;

// Request types
export interface UpdateIntegrationRequest {
  clientId?: string | null; // null to unlink from client
  syncEnabled?: boolean;
  syncRepos?: string[];
  syncAssigneesOnly?: boolean;
  webhooksEnabled?: boolean;
}

// GitHub repository info
export interface GitHubRepository {
  id: number;
  name: string;
  fullName: string;
  private: boolean;
  defaultBranch: string;
  url: string;
}

// Sync result
export interface SyncResult {
  message: string;
  tasksCreated: number;
  tasksUpdated: number;
  errors: string[];
}

export interface User {
  id: string;
  email: string;
  name: string;
  hourlyRate?: number;
  timezone?: string;

  // Company Information
  companyName?: string;
  companyAddress?: string;
  tradeLicenseNumber?: string;
  vatNumber?: string;
  taxId?: string;

  // Contact Details
  phone?: string;
  website?: string;

  // Payment Information
  bankName?: string;
  bankAccountName?: string;
  iban?: string;
  swiftBic?: string;
  paymentMethods?: string[];
  paymentInstructions?: string;

  // Invoice Defaults
  defaultCurrency?: string;
  defaultPaymentTerms?: string;
  defaultTermsAndConditions?: string;
  defaultTaxRate?: number;
  defaultHourlyRate?: number;

  // Branding
  logoUrl?: string;

  createdAt?: string;
  updatedAt?: string;
}

export interface Client {
  id: string;
  name: string; // Internal identifier/reference name
  businessName?: string; // Official business/person name for invoices
  type: 'business' | 'private';
  currency?: Currency; // Preferred currency for invoices
  contactInfo?: string;
  email?: string;
  vatNumber?: string;
  street?: string;
  city?: string;
  zipCode?: string;
  country?: string;
  hourlyRate?: number;
  defaultTaxRate?: number;
  vatReverseCharge?: boolean; // Apply VAT reverse charge for EU B2B transactions
  vatReverseChargeMessage?: string; // Custom VAT reverse charge message for invoices
  termsAndConditions?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Project {
  id: string;
  clientId: string;
  name: string;
  type: 'client' | 'side-project';
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TaskComment {
  id: string;
  text: string;
  createdAt: string;
}

export interface Task {
  id: string;
  title: string;
  description?: string;
  clientId?: string;
  projectId?: string;
  priority: Priority;
  status: TaskStatus;
  deadline?: string;
  comments?: TaskComment[];
  // Integration fields
  origin: TaskOrigin;
  createdBy: string;
  externalId?: string;
  externalUrl?: string;
  externalData?: ExternalTaskData;
  lastSyncedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Task API request/response types
export interface CreateTaskRequest {
  title: string;
  description?: string;
  clientId?: string;
  projectId?: string;
  priority: Priority;
  status: TaskStatus;
  deadline?: string;
  // Optional integration fields for synced tasks
  origin?: TaskOrigin;
  externalId?: string;
  externalUrl?: string;
  externalData?: ExternalTaskData;
}

export interface UpdateTaskRequest {
  title?: string;
  description?: string;
  clientId?: string;
  projectId?: string;
  priority?: Priority;
  status?: TaskStatus;
  deadline?: string;
}

export interface TaskFilters {
  clientId?: string;
  projectId?: string;
  status?: TaskStatus;
  priority?: Priority;
  origin?: TaskOrigin;
  startDate?: string;
  endDate?: string;
}

export interface TasksResponse {
  tasks: Task[];
  total: number;
}

export interface TimeEntry {
  id: string;
  userId?: string;
  clientId?: string;
  projectId?: string;
  taskId?: string;
  duration: number;
  date: string;
  startTime?: string;
  description?: string;
  type?: TimerType;
  billable?: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface Expense {
  id: string;
  userId: string;
  amount: number;
  category: string;
  clientId?: string;
  projectId?: string;
  isRecurring: boolean;
  date: string;
  receiptUrl?: string;
  notes?: string;
  createdAt: string;
}

export interface ActionItem {
  id: string;
  task: string;
  assignee: string;
  dueDate: string;
  completed: boolean;
}

export interface Meeting {
  id: string;
  userId: string;
  clientId?: string;
  projectId?: string;
  date: string;
  participants: string[];
  transcript?: string;
  summary?: string;
  actionItems?: ActionItem[];
  linkedTasks?: string[];
  createdAt: string;
}

// 'md' = man days. 1 man day is billed as the client's hourly rate × HOURS_PER_MAN_DAY.
export type LineItemUnit = 'hours' | 'items' | 'days' | 'md';

// Number of billable hours represented by a single man day (MD) unit.
export const HOURS_PER_MAN_DAY = 8;

export interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unit: LineItemUnit;
  unitPrice: number;
  amount: number;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  clientId: string;
  projectId?: string;
  issueDate: string;
  dueDate: string;
  servicePeriodStart?: string;
  servicePeriodEnd?: string;
  status: InvoiceStatus;
  lineItems: LineItem[];
  subtotal: number;
  taxRate?: number;
  taxAmount?: number;
  discount?: number;
  total: number;
  paidAmount: number;
  paidDate?: string;
  paymentMethod?: PaymentMethod;
  pdfUrl?: string;
  pdfKey?: string;
  notes?: string;
  termsAndConditions?: string;
  sentToAccounting?: boolean;
  sentToAccountingAt?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  client?: Client;
}

// ============================================================================
// Task Helper Functions
// ============================================================================

/**
 * Check if a task is overdue
 */
export function isTaskOverdue(task: Task): boolean {
  if (!task.deadline || task.status === 'completed') {
    return false;
  }

  const today = new Date().toISOString().split('T')[0];
  return task.deadline < today;
}

/**
 * Check if a task is due today
 */
export function isTaskDueToday(task: Task): boolean {
  if (!task.deadline || task.status === 'completed') {
    return false;
  }

  const today = new Date().toISOString().split('T')[0];
  return task.deadline === today;
}

/**
 * Get priority color for badges
 */
export function getPriorityColor(priority: Priority): string {
  switch (priority) {
    case 'high':
      return 'red';
    case 'medium':
      return 'orange';
    case 'low':
      return 'green';
    default:
      return 'gray';
  }
}

/**
 * Get status color for badges
 */
export function getTaskStatusColor(status: TaskStatus): string {
  switch (status) {
    case 'not-started':
      return 'gray';
    case 'in-progress':
      return 'blue';
    case 'completed':
      return 'green';
    default:
      return 'gray';
  }
}

/**
 * Get status label for display
 */
export function getTaskStatusLabel(status: TaskStatus): string {
  switch (status) {
    case 'not-started':
      return 'Not Started';
    case 'in-progress':
      return 'In Progress';
    case 'completed':
      return 'Completed';
    default:
      return 'Unknown';
  }
}

/**
 * Format due date for display
 */
export function formatDueDate(deadline?: string): string {
  if (!deadline) return 'No due date';

  const date = new Date(deadline);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const taskDate = new Date(deadline);
  taskDate.setHours(0, 0, 0, 0);

  const diffTime = taskDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
  if (diffDays <= 7) return `In ${diffDays} days`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  });
}

// ============================================================================
// Client Analytics Types
// ============================================================================

/**
 * Aggregate analytics for all clients
 */
export interface ClientsAnalytics {
  totalEarnings: number;
  totalHours: number;
  activeTasks: number;
  completedTasks: number;
  totalInvoices: number;
  paidInvoices: number;
  unpaidAmount: number;
  currency: string;
  lastUpdated: string; // ISO timestamp
}

/**
 * Analytics for a specific client
 */
export interface ClientAnalytics {
  clientId: string;
  totalEarnings: number;
  totalHours: number;
  activeTasks: number;
  completedTasks: number;
  totalInvoices: number;
  paidInvoices: number;
  unpaidAmount: number;
  lastInvoiceDate?: string; // ISO timestamp
  lastTimeEntryDate?: string; // ISO timestamp
  currency: string;
  lastUpdated: string; // ISO timestamp
}

/**
 * Client with embedded analytics for list views
 */
export interface ClientWithAnalytics extends Client {
  analytics?: ClientAnalytics;
}